/**
 * Pure portfolio math shared by screens — no fetching here.
 */
const EMPTY = [];

/**
 * The unstressed run, by its canonical slug.
 *
 * `standard_var` is the risk engine's default parameter configuration — what
 * `get_default_risk_scenario_name` returns for RiskType.RISK, and the scenario
 * every unstressed row on this backend is written under. It is matched on
 * `scenarioValue` because that is the identity the tables are keyed on;
 * `scenarioName` is a display copy the backend renames freely.
 */
export const STANDARD_VAR = 'standard_var';

/**
 * The scenario the REALISED pass writes under, which is not the base one.
 *
 * `calculate_realized_portfolio_risk` pins it — `risk_config_id` "parametric_full",
 * `scenario_name` "conservative_portfolio_var_model" — and every realised figure
 * on this backend is stored against it. It shares `risk_type` RISK with
 * `standard_var`, so a series asked for by risk type alone gets both and the
 * forecast rows outnumber the realised ones: a metric that has both a `realized*`
 * and a `forecast*` field then draws mostly forecast with a realised point or
 * two mixed in. Anything charting a realised figure has to name this scenario.
 */
export const REALIZED_VAR = 'conservative_portfolio_var_model';

export function isBaseScenario(row) {
  return String(row?.scenarioValue || '').trim().toLowerCase() === STANDARD_VAR;
}

/** A stress window, by the backend's own single rule. */
export function isStressScenario(row) {
  return String(row?.riskType || '').trim().toUpperCase() === 'STRESS';
}

/**
 * A portfolio's headline risk row: the newest `standard_var` run, or nothing.
 *
 * `riskMetrics` is a LIST — one row per configured scenario per run — not the
 * single object its name suggests. Reading a field straight off it yields
 * undefined, which is silent: a filter on `riskMetrics?.riskVsTarget` simply
 * never matches and the panel counting it reports a confident zero. So every
 * caller comes through here.
 *
 * There is deliberately NO fallback to "the newest row of any kind". That is
 * how a subject with no unstressed run ends up quoting a 2008-replay's
 * volatility as its headline and decomposing that scenario's holdings under a
 * panel labelled base case. Null instead, so the screen shows nothing rather
 * than the wrong thing.
 */
export function baseRiskRow(riskMetrics) {
  const rows = Array.isArray(riskMetrics) ? riskMetrics : EMPTY;
  if (!rows.length) return null;
  return [...rows]
    .sort((a, b) => String(b.latestDate || b.runDate || '').localeCompare(String(a.latestDate || a.runDate || '')))
    .find(isBaseScenario) ?? null;
}

/**
 * A portfolio that holds the money for one goal.
 *
 * Read off the symbol, which is the backend's own naming: a goal portfolio is
 * written as `{ownerId}_{goal}_goal`, the convention the forecast-risk handler
 * parses an owner out of too.
 *
 * `goalId` would be the better marker and cannot be used. It comes back NULL on
 * every row of the book query — the one the client screens are built on, which
 * asks with `allUsers: true` — while the same portfolios carry real goal ids
 * when fetched one user at a time. `goalData` is null on both paths, so a goal's
 * name, target and funded status are not available from `getPortfolios` at all;
 * those come from `getGoals`, which is why a client can show four goals and no
 * goal PLAN. The two are different things and the wording keeps them apart.
 */
export const isGoalPortfolio = (p) =>
  String(p?.securitySymbol || '').toLowerCase().endsWith('_goal');

/**
 * One asset class, named the same way whoever is talking.
 *
 * The backend uses two vocabularies for the same thing and they do not match:
 * a portfolio's own `classifications.assetClasses` carry the security master's
 * spelling — lower case and underscored, `equity`, `fixed_income` — while the
 * policy side (a goal's `benchmarkPortfolio.allocations`, a model's
 * `broadAllocations`) carries display names, `Equity`, `Fixed Income`, `Gold`.
 *
 * Joining those on the raw string silently matched nothing: every actual weight
 * lost its target, every target appeared again as a row with 0% held, and the
 * drift read off that said a client with 82% equity was 45 points UNDER a 45%
 * equity target — which then wrote itself into the policy-drift figure, the
 * action items and the client commentary. Both sides go through this.
 */
export const assetClassKey = (name) => String(name ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

/** Goals against everything else a client holds, e.g. a brokerage account. */
export function portfolioScope(portfolios) {
  const rows = Array.isArray(portfolios) ? portfolios : EMPTY;
  const goals = rows.filter(isGoalPortfolio).length;
  return { goals, other: rows.length - goals, total: rows.length };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What a client holds, in words: "4 goals", "1 portfolio", "3 goals · 1
 * portfolio". Shared so the header, the holdings card and the clients table
 * cannot describe the same client three different ways.
 */
export function portfolioScopeLabel(scope) {
  if (!scope?.total) return 'no portfolios';
  if (!scope.other) return plural(scope.goals, 'goal', 'goals');
  if (!scope.goals) return plural(scope.other, 'portfolio', 'portfolios');
  return `${plural(scope.goals, 'goal', 'goals')} · ${plural(scope.other, 'portfolio', 'portfolios')}`;
}

/** Pairwise holding overlap across a client's portfolios — the design's overlap matrix. */
export function overlapMatrix(portfolios) {
  const sets = portfolios.map((p) => ({
    name: p.portfolioName || p.securitySymbol,
    weights: new Map(
      (p.holdings ?? EMPTY).map((h) => [h.holdingSecuritySymbol, Number(h.weight) || 0]),
    ),
  }));
  return sets.map((a) =>
    sets.map((b) => {
      if (a === b) return 100;
      let shared = 0;
      a.weights.forEach((w, k) => {
        const other = b.weights.get(k);
        if (other != null) shared += Math.min(w, other);
      });
      return Math.round(shared * 100);
    }),
  );
}

/**
 * The classification axes a screen can offer, in the order they answer a
 * question about a holding: what business is it in, what kind of instrument,
 * then where in the world.
 *
 * `industries` and `regions` come back null on every scheme sampled so far.
 * They are listed anyway — an axis with no rows is simply dropped, so the day
 * a security carries them they appear on their own.
 */
const CLASSIFICATION_AXES = [
  ['sectors', 'Sectors'],
  ['industries', 'Industries'],
  ['assetClasses', 'Asset classes'],
  ['securityTypes', 'Security types'],
  ['countries', 'Countries'],
  ['regions', 'Regions'],
  ['currencies', 'Currencies'],
];

/**
 * One `PortfolioClassificationSummaryType` as the exposure panel reads it.
 *
 * Serves a scheme and a model alike: `getMetaMutualFund.classifications` and
 * `getPortfolio.classifications` are the same type, so the shape a screen sees
 * does not depend on which record answered.
 *
 * Weights arrive as fractions of the WHOLE subject on both levels and are
 * scaled here, once — a holding's 0.20 under EQUITY is 20% of the fund, not 20%
 * of the equity sleeve, so the holdings under a bucket sum to the bucket.
 *
 * `hasBenchmark` says whether an over/underweight column has anything to show:
 * the fields are published but null today, and a column of em-dashes would
 * read as "in line with the benchmark" rather than "not measured".
 */
export function classificationAxes(classifications) {
  if (!classifications) return EMPTY;
  const pctOf = (v) => (v == null ? null : Number(v) * 100);

  return CLASSIFICATION_AXES.map(([key, label]) => {
    const rows = classifications[key] ?? EMPTY;
    const buckets = rows
      .map((r) => ({
        label: r.classificationValue || 'Unclassified',
        weightPct: pctOf(r.weight) ?? 0,
        marketValue: Number(r.marketValue) || 0,
        benchmarkPct: pctOf(r.benchmarkWeight),
        relativePct: pctOf(r.relativeWeight),
        holdings: (r.holdings ?? EMPTY)
          .map((h) => ({
            symbol: h.holdingSecuritySymbol,
            name: h.name || h.holdingSecuritySymbol,
            weightPct: pctOf(h.weight) ?? 0,
            marketValue: Number(h.marketValue) || 0,
            benchmarkPct: pctOf(h.benchmarkWeight),
            relativePct: pctOf(h.relativeWeight),
          }))
          .sort((a, b) => b.weightPct - a.weightPct),
      }))
      .sort((a, b) => b.weightPct - a.weightPct);

    return {
      id: key,
      label,
      buckets,
      covered: buckets.reduce((t, b) => t + b.weightPct, 0),
      max: Math.max(0.0001, ...buckets.map((b) => b.weightPct)),
      hasBenchmark: buckets.some((b) => b.benchmarkPct != null),
    };
  }).filter((axis) => axis.buckets.length > 0);
}

/**
 * One bucket's holdings as HoldingDrill rows. Read against the benchmark, the
 * figure is the active weight and the holding's own weight sits beside it;
 * otherwise the weight IS the figure, so it is not printed twice.
 */
export const bucketDrillRows = (bucket, relative = false) => (bucket?.holdings ?? EMPTY).map((h) => ({
  symbol: h.symbol,
  name: h.name,
  weight: relative ? h.weightPct : null,
  value: relative ? h.relativePct : h.weightPct,
}));

/**
 * Which history a holding's WEIGHT charts from, for this subject.
 *
 * The three sources answer the same question for different subjects and none
 * holds anything for another's symbols, so the choice is the caller's and is
 * made here once rather than in each table that shows a weight:
 *
 *   holding.weight      a portfolio's own dated rows      daily
 *   fundFactor.weight   a scheme's factor-contribution    daily, stepping on
 *                       rows, which carry the weight      each disclosure
 *   fundHolding.weight  the AMC disclosures themselves    one point per filing
 *
 * A scheme prefers the factor rows: on the fund sampled they give 241 points
 * across a year against 1 from the disclosure history, both stepping on the
 * same monthly cycle because they describe the same filing. `scored` is false
 * for a position the factor model does not reach — cash, repo, government
 * paper — and those fall back to the disclosure, which is all they have.
 */
export const holdingWeightMetric = ({ isModel, scored }) => {
  if (isModel) return 'holding.weight';
  return scored ? 'fundFactor.weight' : 'fundHolding.weight';
};

/**
 * Which bar each contribution field belongs under.
 *
 * Keyed by the label `fundFactors` gives the bars, so a factor is named once
 * and the drill-down cannot end up hanging beneath the wrong one. Momentum has
 * two entries because the run publishes it as two scores and the combined field
 * is null everywhere.
 */
const CONTRIBUTION_KEY = {
  'ST momentum': 'shortTermMomentumScoreContribution',
  'LT momentum': 'longTermMomentumScoreContribution',
  Value: 'valueScoreContribution',
  Quality: 'qualityScoreContribution',
  Growth: 'growthScoreContribution',
  'Low volatility': 'lowVolatilityScoreContribution',
  'High volatility': 'highVolatilityScoreContribution',
};

/**
 * Per-holding factor contributions, grouped under the bar each belongs to.
 *
 * A contribution is the holding's weight times its own loading, so unlike a
 * loading it is signed against the position: two holdings can carry the same
 * value tilt and pull the fund by very different amounts. Rows are ordered by
 * magnitude rather than by signed value, because the question a reader opens
 * this with is which positions move the number, not which are most positive.
 *
 * `total` is the bottom-up sum and is deliberately NOT the bar it sits under.
 * The bars are the fund's exposure measured against its benchmark and come off
 * an earlier factor run; these are absolute and off the later one. On HDFC
 * Flexi Cap the two do not even share a sign — the Value bar reads −0.05 while
 * these sum to +0.13 — so the panel states this total in its own words rather
 * than letting it read as a subtotal of the bar.
 *
 * `weight` on this record is a fraction while a scheme's `holdings.weight` is a
 * percentage; both are normalised to a percentage here so nothing downstream
 * has to know which record it came from. Each row is already a HoldingDrill
 * row — `{ symbol, name, weight, value }`.
 */
/**
 * symbol → name off the record's holdings, for a contribution row that does
 * not carry its own `name`. Keyed `securitySymbol` on a scheme and
 * `holdingSecuritySymbol` on a portfolio; a holding with no name is left out,
 * so the caller falls through to the symbol.
 */
const holdingNames = (fund) => new Map((fund?.holdings ?? EMPTY)
  .map((h) => [h.securitySymbol ?? h.holdingSecuritySymbol, h.name])
  .filter(([symbol, name]) => symbol && name));

export function factorContributions(fund) {
  const rows = fund?.holdingFactorContributions ?? EMPTY;
  if (!rows.length) return null;

  const nameOf = holdingNames(fund);
  const universe = fund.holdings?.length ?? rows.length;
  const asOfDate = rows.find((r) => r.asOfDate)?.asOfDate ?? null;

  const out = {};
  for (const [label, key] of Object.entries(CONTRIBUTION_KEY)) {
    const items = rows
      .filter((r) => r[key] != null && Number.isFinite(Number(r[key])))
      .map((r) => ({
        symbol: r.holdingSecuritySymbol,
        name: r.name ?? nameOf.get(r.holdingSecuritySymbol) ?? r.holdingSecuritySymbol,
        value: Number(r[key]),
        weight: r.weight == null ? null : Number(r.weight) * 100,
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    if (!items.length) continue;
    out[label] = {
      rows: items,
      total: items.reduce((a, x) => a + x.value, 0),
      scored: items.length,
      universe,
      asOfDate,
      /* Bars scale off the largest absolute contribution, so the biggest mover
         fills the row and everything else reads against it. Floored so a fund
         whose contributions are all zero cannot divide by nothing. */
      max: Math.max(1e-9, ...items.map((x) => Math.abs(x.value))),
    };
  }
  return Object.keys(out).length ? out : null;
}

/**
 * What carries a scheme's RISK, position by position — the risk sibling of
 * `factorContributions` above.
 *
 * `betaContribution` is queried on every scheme and was never read: it is the
 * holding's weight times its own beta, so the rows sum to the fund's beta and a
 * row's share of that sum is its share of the fund's market risk. That is the
 * only per-holding risk decomposition a SCHEME has — `getPortfolio` resolves an
 * account row and answers "Portfolio not found" for a fund symbol, so the Euler
 * decomposition the risk engine writes for a portfolio is not available here.
 *
 * `lowVolatilityScoreContribution` rides along as the second reading: beta says
 * how much of the market's move the position carries, the volatility tilt says
 * whether the name is a steady one or a jumpy one. Both are signed against the
 * position, so a short-of-benchmark tilt reads negative rather than small.
 *
 * Returns null when the factor run has scored none of the holdings, which is a
 * gap in the scores rather than a fund with no risk — the panel says which.
 */
export function schemeRiskContributions(fund) {
  const rows = fund?.holdingFactorContributions ?? EMPTY;
  if (!rows.length) return null;

  const nameOf = holdingNames(fund);
  const asOfDate = rows.find((r) => r.asOfDate)?.asOfDate ?? null;

  const series = (key) => {
    const items = rows
      .filter((r) => r[key] != null && Number.isFinite(Number(r[key])))
      .map((r) => ({
        symbol: r.holdingSecuritySymbol,
        name: r.name ?? nameOf.get(r.holdingSecuritySymbol) ?? r.holdingSecuritySymbol,
        value: Number(r[key]),
        weightPct: r.weight == null ? null : Number(r.weight) * 100,
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    if (!items.length) return null;
    const total = items.reduce((a, x) => a + x.value, 0);
    return {
      rows: items,
      total,
      /* Shares are taken against the sum of the ABSOLUTE contributions, so a
         fund with offsetting tilts cannot hand a single name a share above
         100% by dividing through a near-zero net. */
      gross: items.reduce((a, x) => a + Math.abs(x.value), 0),
      scored: items.length,
      universe: fund.holdings?.length ?? items.length,
      max: Math.max(1e-9, ...items.map((x) => Math.abs(x.value))),
    };
  };

  const beta = series('betaContribution');
  const lowVolatility = series('lowVolatilityScoreContribution');
  if (!beta && !lowVolatility) return null;
  return { beta, lowVolatility, asOfDate };
}

/**
 * The positions a fund is most ACTIVE in — its bets against the index.
 *
 * Active risk is what the fund does differently, so the drivers of it are the
 * positions whose weight differs most from the benchmark's. Rows come from the
 * multifactor run because that is the only source carrying a benchmark weight
 * per stock.
 *
 * `activeShare` here is Σ|active weight| / 2 over the HELD names only. Index
 * constituents the fund does not own are inside the fund-level factor cells but
 * are not served as rows, so this is a floor on the true active share, never
 * the whole of it — every caller says so beside the figure.
 */
export function activeWeightDrivers(multifactorHoldings) {
  const rows = (multifactorHoldings ?? EMPTY)
    .map((h) => {
      const weight = Number(h.weight);
      const bench = h.benchmarkWeight == null ? 0 : Number(h.benchmarkWeight);
      if (!Number.isFinite(weight)) return null;
      return {
        symbol: h.securitySymbol,
        name: h.name || h.securitySymbol,
        weight,
        benchmarkWeight: Number.isFinite(bench) ? bench : 0,
        active: weight - (Number.isFinite(bench) ? bench : 0),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.active) - Math.abs(a.active));
  if (!rows.length) return null;

  const gross = rows.reduce((t, r) => t + Math.abs(r.active), 0);
  return {
    rows,
    gross,
    activeShare: gross / 2,
    overweights: rows.filter((r) => r.active > 0).length,
    underweights: rows.filter((r) => r.active < 0).length,
    max: Math.max(1e-9, ...rows.map((r) => Math.abs(r.active))),
  };
}

/**
 * A portfolio's own valuation multiple, from the multiples of what it holds.
 *
 * The weighted HARMONIC mean, not the arithmetic one, because a multiple is a
 * ratio: a portfolio's P/E is its price over its earnings, which is
 * 1 / Σ(wᵢ / PEᵢ) — the reciprocal of its weighted earnings yield. The
 * arithmetic mean answers a different question and is wrecked by one bad
 * print, and the master has them: Infosys currently carries a P/E of 1,374,
 * which would lift a 24× fund to 40× on an arithmetic average and leaves the
 * harmonic one at 24.4.
 *
 * Non-positive multiples are dropped rather than inverted — a loss-making
 * company has no meaningful P/E, and a negative one would net off a positive.
 * `covered` is the weight that actually carried a figure, so a reader can see
 * how much of the fund the number speaks for.
 */
export function weightedHarmonic(rows, pick) {
  let reciprocal = 0;
  let covered = 0;
  for (const row of rows) {
    const value = pick(row);
    const weight = Number(row.weight);
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) continue;
    if (!Number.isFinite(weight) || weight <= 0) continue;
    reciprocal += weight / Number(value);
    covered += weight;
  }
  return { value: reciprocal > 0 ? covered / reciprocal : null, covered };
}

/**
 * The plain weighted average, for figures that are already per-rupee flows —
 * a dividend yield or a return on equity averages correctly this way, where a
 * price multiple does not.
 */
export function weightedMean(rows, pick) {
  let total = 0;
  let covered = 0;
  for (const row of rows) {
    const value = pick(row);
    const weight = Number(row.weight);
    if (value == null || !Number.isFinite(Number(value))) continue;
    if (!Number.isFinite(weight) || weight <= 0) continue;
    total += weight * Number(value);
    covered += weight;
  }
  return { value: covered > 0 ? total / covered : null, covered };
}

/**
 * Active share — how much of a book is not its benchmark.
 *
 * Half the sum of the absolute weight differences across the union of both
 * sides, which is the standard definition and lands between 0% (the index
 * exactly) and 100% (nothing in common). The halving is what stops every
 * overweight being counted twice, once as its own excess and again as the
 * underweight it forces somewhere else.
 *
 * Both sides are taken as fractions. A name held by one and not the other
 * counts at its full weight, which is the point — that is the purest form of
 * an active decision.
 */
export function activeShare(fundRows, benchRows) {
  const bench = new Map();
  benchRows.forEach((r) => {
    const w = Number(r.weight);
    if (r.symbol && Number.isFinite(w)) bench.set(r.symbol, w);
  });
  const seen = new Set();
  let total = 0;
  fundRows.forEach((r) => {
    const w = Number(r.weight);
    if (!r.symbol || !Number.isFinite(w)) return;
    seen.add(r.symbol);
    total += Math.abs(w - (bench.get(r.symbol) ?? 0));
  });
  bench.forEach((w, symbol) => {
    if (!seen.has(symbol)) total += Math.abs(w);
  });
  return total / 2;
}

/**
 * Which holdings make a book dearer or cheaper than its benchmark.
 *
 * A P/E cannot be decomposed — it is a ratio — but its reciprocal can. A
 * book's earnings yield is Σ wᵢ/PEᵢ, a plain weighted sum, so the gap between
 * two books' yields breaks into one additive term per name:
 *
 *     termᵢ = wFundᵢ / xᵢ − wBenchᵢ / xᵢ
 *
 * A positive term adds earnings yield against the benchmark, which makes the
 * fund CHEAPER; a negative one makes it dearer. The terms sum exactly to the
 * yield gap, so the ranking explains the headline multiple rather than
 * decorating it.
 *
 * Names held by only one side count at their full weight — an index heavyweight
 * you do not own moves this as surely as an overweight you do.
 *
 * Returned in basis points of earnings yield, which is the unit the numbers
 * are legible in: a 38bp term is a third of a percent of yield.
 */
export function valuationGap(fundRows, benchRows, pick) {
  const yieldOf = (row) => {
    const x = Number(pick(row));
    const w = Number(row.weight);
    if (!Number.isFinite(x) || x <= 0 || !Number.isFinite(w) || w <= 0) return null;
    return w / x;
  };

  /* Each side is normalised by the weight that actually carried a multiple, so
     the terms sum to the gap between the two headline figures — those are
     harmonic means over the priced part, not over the whole book. Without this
     the unpriced weight would read as zero yield and both sides would look
     dearer than the panel above says they are. */
  const covered = (rows) => rows.reduce((t, r) => t + (yieldOf(r) == null ? 0 : Number(r.weight)), 0);
  const fundCover = covered(fundRows) || 1;
  const benchCover = covered(benchRows) || 1;

  const bench = new Map();
  benchRows.forEach((r) => {
    if (!r.symbol) return;
    const y = yieldOf(r);
    bench.set(r.symbol, { row: r, y: y == null ? null : y / benchCover });
  });

  const seen = new Set();
  const rows = [];
  fundRows.forEach((r) => {
    if (!r.symbol) return;
    seen.add(r.symbol);
    const own = yieldOf(r);
    const mine = own == null ? null : own / fundCover;
    const theirs = bench.get(r.symbol)?.y ?? 0;
    if (mine == null && !theirs) return;
    rows.push({ symbol: r.symbol, name: r.name, held: true, value: ((mine ?? 0) - theirs) * 10000 });
  });
  bench.forEach(({ row, y }, symbol) => {
    if (seen.has(symbol) || y == null) return;
    rows.push({ symbol, name: row.name, held: false, value: -y * 10000 });
  });

  rows.sort((a, b) => b.value - a.value);
  return {
    rows,
    total: rows.reduce((t, r) => t + r.value, 0),
    max: Math.max(1e-9, ...rows.map((r) => Math.abs(r.value))),
  };
}
